import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Trash2, Loader2, CheckCircle2, Clock, XCircle, Lock } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { QtyControl } from "@/components/site/CartDrawer";
import { Seo } from "@/components/site/Seo";
import { Img } from "@/components/site/Img";
import { Field, emailOk } from "@/components/forms/FormKit";
import { api, errText, money, PHONE } from "@/lib/api";

function useValidatedCart() {
  const { items } = useCart();
  const [res, setRes] = useState(null);
  const sig = items.map((i) => `${i.key}:${i.qty}`).join(",");
  useEffect(() => {
    if (!items.length) return setRes(null);
    api.post("/cart/validate", { items: items.map(({ product_id, qty, options }) => ({ product_id, qty, options })) }).then((r) => setRes(r.data)).catch(() => setRes(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  return res;
}

function Summary({ v }) {
  const { subtotal } = useCart();
  return (
    <div className="panel p-6" data-testid="order-summary">
      <p className="eyebrow mb-4">Order summary</p>
      <div className="space-y-2 font-mono text-sm">
        <div className="flex justify-between"><span className="text-slate-400">Subtotal</span><span data-testid="summary-subtotal">{money(v?.subtotal ?? subtotal)}</span></div>
        <div className="flex justify-between"><span className="text-slate-400">UK delivery</span><span>{v?.shipping ? money(v.shipping) : "Free"}</span></div>
        <div className="flex justify-between border-t border-[color:var(--line)] pt-3 text-base"><span>Total</span><span className="text-[color:var(--ice)]" data-testid="summary-total">{money(v?.total ?? subtotal)}</span></div>
      </div>
      <p className="mt-3 text-xs text-slate-500">Prices in GBP. Any applicable tax is shown on the secure payment page.</p>
      {v?.problems?.length > 0 && <ul className="mt-4 space-y-1 text-sm text-amber-400" data-testid="cart-problems">{v.problems.map((p) => <li key={p}>{p}</li>)}</ul>}
    </div>
  );
}

export function CartPage() {
  const { items, remove } = useCart();
  const v = useValidatedCart();
  return (
    <section className="container-x py-14" data-testid="cart-page">
      <Seo path="/cart" fallbackTitle="Basket | Jorvik Tuning" noindex />
      <h1 className="font-display text-5xl font-bold uppercase">Basket</h1>
      {!items.length ? (
        <div className="mt-10 panel p-10 text-slate-400" data-testid="cart-empty">Your basket is empty. <Link to="/shop" className="text-[color:var(--ice)] underline">Browse the shop</Link>.</div>
      ) : (
        <div className="mt-10 grid gap-10 lg:grid-cols-[1fr_360px]">
          <ul className="divide-y divide-[color:var(--line)] border-y border-[color:var(--line)]">
            {items.map((i) => (
              <li key={i.key} className="flex gap-5 py-5" data-testid={`cart-item-${i.slug}`}>
                <div className="h-24 w-24 shrink-0 bg-[color:var(--bg-3)]">{i.image && <Img src={i.image} alt={i.name} sizes="96px" className="h-full w-full object-cover" />}</div>
                <div className="flex flex-1 flex-col justify-between gap-3 sm:flex-row sm:items-center">
                  <div><Link to={`/product-page/${i.slug}`} className="font-medium hover:text-[color:var(--ice)]">{i.name}</Link>{Object.keys(i.options || {}).length > 0 && <p className="text-xs text-slate-500">{Object.entries(i.options).map(([a, b]) => `${a}: ${b}`).join(" · ")}</p>}<p className="mt-1 font-mono text-sm text-slate-400">{money(i.price)}</p></div>
                  <div className="flex items-center gap-4">
                    <QtyControl item={i} testPrefix="cart" />
                    <span className="w-24 text-right font-mono">{money(i.price * i.qty)}</span>
                    <button onClick={() => remove(i.key)} className="p-2 text-slate-500 hover:text-red-400" aria-label="Remove" data-testid={`cart-remove-${i.slug}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="space-y-4">
            <Summary v={v} />
            <Link to="/checkout" className="btn-ice w-full" data-testid="cart-checkout-button">Checkout</Link>
          </div>
        </div>
      )}
    </section>
  );
}

const EMPTY = { name: "", email: "", phone: "", company: "", line1: "", line2: "", city: "", postcode: "", country: "United Kingdom", notes: "" };

export function CheckoutPage() {
  const { items } = useCart();
  const v = useValidatedCart();
  const [f, setF] = useState(EMPTY);
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("stripe");
  const [terms, setTerms] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/payments/providers").then((r) => setProviders(r.data)); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (f.name.trim().length < 2) er.name = "Required";
    if (!emailOk(f.email)) er.email = "Valid email required";
    if (!f.line1.trim()) er.line1 = "Required";
    if (!f.city.trim()) er.city = "Required";
    if (f.postcode.trim().length < 3) er.postcode = "Required";
    if (!terms) er.terms = "Please accept the terms";
    setErrors(er);
    if (Object.keys(er).length) return;
    setBusy(true); setMsg("");
    try {
      const r = await api.post("/checkout", {
        items: items.map(({ product_id, qty, options }) => ({ product_id, qty, options })),
        customer: { name: f.name, email: f.email, phone: f.phone, company: f.company },
        shipping_address: { line1: f.line1, line2: f.line2, city: f.city, postcode: f.postcode, country: f.country },
        notes: f.notes, provider, origin_url: window.location.origin, accept_terms: terms,
      });
      window.location.href = r.data.redirect_url;
    } catch (err) {
      setMsg(errText(err)); setBusy(false);
    }
  };
  if (!items.length) return <section className="container-x py-24" data-testid="checkout-empty"><Seo path="/checkout" noindex /><p className="text-slate-400">Your basket is empty. <Link className="text-[color:var(--ice)] underline" to="/shop">Browse the shop</Link>.</p></section>;
  const inp = (k, label, props = {}) => (
    <Field label={label} id={`checkout-${k}`} error={errors[k]}><input id={`checkout-${k}`} className="field" value={f[k]} onChange={set(k)} data-testid={`checkout-${k}-input`} {...props} /></Field>
  );
  return (
    <section className="container-x py-14" data-testid="checkout-page">
      <Seo path="/checkout" fallbackTitle="Checkout | Jorvik Tuning" noindex />
      <h1 className="font-display text-5xl font-bold uppercase">Checkout</h1>
      <form onSubmit={submit} noValidate className="mt-10 grid gap-10 lg:grid-cols-[1fr_380px]">
        <div className="space-y-10">
          <fieldset className="grid gap-5 sm:grid-cols-2"><legend className="eyebrow mb-5">Your details</legend>
            {inp("name", "Full name *", { autoComplete: "name" })}{inp("email", "Email *", { type: "email", autoComplete: "email" })}
            {inp("phone", "Phone", { type: "tel", autoComplete: "tel" })}{inp("company", "Company", { autoComplete: "organization" })}
          </fieldset>
          <fieldset className="grid gap-5 sm:grid-cols-2"><legend className="eyebrow mb-5">Delivery address</legend>
            <div className="sm:col-span-2">{inp("line1", "Address line 1 *", { autoComplete: "address-line1" })}</div>
            <div className="sm:col-span-2">{inp("line2", "Address line 2", { autoComplete: "address-line2" })}</div>
            {inp("city", "Town / city *", { autoComplete: "address-level2" })}{inp("postcode", "Postcode *", { autoComplete: "postal-code" })}
            {inp("country", "Country", { autoComplete: "country-name" })}
          </fieldset>
          <Field label="Order notes" id="checkout-notes"><textarea id="checkout-notes" rows={3} className="field" value={f.notes} onChange={set("notes")} data-testid="checkout-notes-input" /></Field>
          <fieldset><legend className="eyebrow mb-5">Payment method</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              {providers.map((p) => (
                <label key={p.key} className={`flex cursor-pointer items-center gap-3 border p-4 transition-colors ${!p.enabled ? "cursor-not-allowed opacity-50" : provider === p.key ? "border-[color:var(--ice)] bg-[color:var(--ice)]/5" : "border-[color:var(--line-2)]"}`} data-testid={`payment-option-${p.key}`} aria-disabled={!p.enabled}>
                  <input type="radio" name="provider" disabled={!p.enabled} checked={provider === p.key} onChange={() => setProvider(p.key)} className="accent-[#7dd3fc]" />
                  <span className="text-sm">{p.label}</span>
                  {!p.enabled && <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-slate-500">Coming soon</span>}
                </label>
              ))}
            </div>
          </fieldset>
        </div>
        <div className="space-y-4 lg:sticky lg:top-28 lg:self-start">
          <ul className="panel divide-y divide-[color:var(--line)] px-6">
            {items.map((i) => <li key={i.key} className="flex justify-between gap-3 py-3 text-sm"><span className="text-slate-300">{i.qty} × {i.name}{Object.keys(i.options || {}).length > 0 && ` (${Object.values(i.options).join(", ")})`}</span><span className="font-mono">{money(i.qty * i.price)}</span></li>)}
          </ul>
          <Summary v={v} />
          <label className="flex items-start gap-3 text-sm text-slate-300">
            <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1 accent-[#7dd3fc]" data-testid="checkout-terms-checkbox" />
            <span>I accept the <Link to="/terms-conditions" className="text-[color:var(--ice)] underline">terms</Link> and <Link to="/shipping-returns" className="text-[color:var(--ice)] underline">returns policy</Link>.</span>
          </label>
          {errors.terms && <p className="text-xs text-red-400" data-testid="checkout-terms-error">{errors.terms}</p>}
          {msg && <p className="border border-red-500/40 bg-red-500/5 p-3 text-sm text-red-300" data-testid="checkout-error">{msg}</p>}
          <button disabled={busy || v?.problems?.length > 0} className="btn-ice w-full disabled:opacity-60" data-testid="checkout-pay-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}{busy ? "Redirecting…" : "Continue to secure payment"}
          </button>
        </div>
      </form>
    </section>
  );
}

export function CheckoutSuccess() {
  const [sp] = useSearchParams();
  const { clear } = useCart();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const order = sp.get("order"), key = sp.get("key");
  useEffect(() => {
    let n = 0, timer, live = true;
    const poll = async () => {
      try {
        const r = await api.get(`/orders/${order}`, { params: { key } });
        if (!live) return;
        setO(r.data);
        if (r.data.payment_status === "paid") return clear();
        if (r.data.payment_status === "pending" && ++n < 15) timer = setTimeout(poll, 2000);
      } catch (e) { live && setErr(errText(e, "We couldn't find that order.")); }
    };
    poll();
    return () => { live = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, key]);
  const paid = o?.payment_status === "paid";
  const Icon = paid ? CheckCircle2 : o && o.payment_status !== "pending" ? XCircle : Clock;
  return (
    <section className="container-x max-w-3xl py-20" data-testid="checkout-success-page">
      <Seo path="/checkout/success" fallbackTitle="Order confirmation | Jorvik Tuning" noindex />
      {err ? <p className="text-red-400" data-testid="order-error">{err}</p> : (
        <>
          <Icon className={`h-12 w-12 ${paid ? "text-emerald-400" : "text-[color:var(--ice)]"}`} />
          <h1 className="mt-6 font-display text-5xl font-bold uppercase" data-testid="order-status-heading">{paid ? "Order confirmed" : o && o.payment_status !== "pending" ? "Payment not completed" : "Confirming payment…"}</h1>
          {o && <p className="mt-4 text-slate-300">Order <span className="font-mono text-[color:var(--ice)]" data-testid="order-number">{o.order_number}</span>{paid ? ` — thanks, ${o.customer_name.split(" ")[0]}. A confirmation email is on its way and we'll be in touch about dispatch.` : ""}</p>}
          {o && (
            <div className="panel mt-10 p-6">
              <ul className="divide-y divide-[color:var(--line)]">{o.items.map((i, k) => <li key={k} className="flex justify-between py-3 text-sm"><span>{i.qty} × {i.name}{Object.keys(i.options || {}).length > 0 && ` (${Object.values(i.options).join(", ")})`}</span><span className="font-mono">{money(i.line_total)}</span></li>)}</ul>
              <div className="mt-3 flex justify-between border-t border-[color:var(--line)] pt-3 font-mono"><span>Total</span><span className="text-[color:var(--ice)]" data-testid="order-total">{money(o.total)}</span></div>
            </div>
          )}
          <p className="mt-8 text-sm text-slate-500">Questions? Call {PHONE} quoting your order number.</p>
          <Link to="/shop" className="btn-ghost mt-8" data-testid="order-continue-shopping">Continue shopping</Link>
        </>
      )}
    </section>
  );
}

export function CheckoutCancel() {
  const nav = useNavigate();
  return (
    <section className="container-x max-w-2xl py-24" data-testid="checkout-cancel-page">
      <Seo path="/checkout/cancel" fallbackTitle="Payment cancelled | Jorvik Tuning" noindex />
      <XCircle className="h-12 w-12 text-amber-400" />
      <h1 className="mt-6 font-display text-5xl font-bold uppercase">Payment cancelled</h1>
      <p className="mt-4 text-slate-400">No payment was taken. Your basket has been kept.</p>
      <div className="mt-8 flex gap-3"><button onClick={() => nav("/checkout")} className="btn-ice" data-testid="cancel-retry">Try again</button><Link to="/cart" className="btn-ghost">View basket</Link></div>
    </section>
  );
}
